import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Building2, CheckCircle, CircleDollarSign, Database, Globe, Search, ShieldAlert, TrendingUp, Users, Zap } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fetchCompaniesAdmin, fetchPlatformDashboard } from "@/lib/adminCompanies";
import { useCompany } from "@/hooks/useCompany";

const summaryCards = [
  ["total_companies", "Total Companies", Building2],
  ["active_companies", "Active Companies", CheckCircle],
  ["suspended_companies", "Suspended Companies", ShieldAlert],
  ["trial_companies", "Trial Companies", TrendingUp],
  ["paid_companies", "Paid Companies", CircleDollarSign],
  ["expired_companies", "Expired Companies", AlertTriangle],
  ["total_users", "Total Users", Users],
  ["total_websites", "Total Websites", Globe],
  ["total_articles", "Total Articles", Database],
  ["total_sources", "Total Sources", Database],
  ["total_published_articles", "Published Articles", CheckCircle],
  ["total_credits_used_this_month", "Credits Used This Month", Zap],
  ["monthly_revenue", "Monthly Revenue", CircleDollarSign],
  ["failed_payments", "Failed Payments", AlertTriangle],
  ["companies_with_errors", "Companies With Errors", ShieldAlert],
  ["companies_near_limits", "Companies Near Limits", TrendingUp],
  ["companies_with_no_activity", "No Activity", AlertTriangle],
] as const;

const chartCards = [
  ["company_growth", "Company Growth", "area"],
  ["revenue_growth", "Revenue Growth", "line"],
  ["credit_consumption", "Credit Consumption", "area"],
  ["article_publishing_trends", "Article Publishing Trends", "line"],
  ["active_users_trends", "Active Users Trends", "area"],
] as const;

const statusFilters = ["active", "suspended", "trial", "unpaid", "archived"];

export default function CompaniesPage() {
  const navigate = useNavigate();
  const { isPlatformOwner } = useCompany();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const dashboard = useQuery({
    queryKey: ["companies-platform-dashboard"],
    queryFn: fetchPlatformDashboard,
  });
  const companiesQuery = useQuery({
    queryKey: ["companies-admin", search, statusFilter],
    queryFn: () => fetchCompaniesAdmin(search || undefined, statusFilter || undefined),
  });

  const companies = companiesQuery.data?.companies ?? [];
  const fallbackSummary = useMemo(() => {
    const active = companies.filter((company) => company.status === "active").length;
    const suspended = companies.filter((company) => company.status === "suspended").length;
    return {
      total_companies: companiesQuery.data?.total ?? companies.length,
      active_companies: active,
      suspended_companies: suspended,
      trial_companies: companies.filter((company) => company.status === "trial").length,
      paid_companies: companies.filter((company) => !["trial", "unpaid", "expired"].includes(company.status)).length,
      expired_companies: companies.filter((company) => ["expired", "unpaid"].includes(company.status)).length,
      total_users: companies.reduce((sum, company) => sum + (company.member_count || 0), 0),
      total_websites: companies.reduce((sum, company) => sum + (company.website_count || 0), 0),
      total_articles: companies.reduce((sum, company) => sum + (company.article_count || 0), 0),
      total_sources: 0,
      total_published_articles: 0,
      total_credits_used_this_month: 0,
      monthly_revenue: 0,
      failed_payments: 0,
      companies_with_errors: 0,
      companies_near_limits: 0,
      companies_with_no_activity: companies.filter((company) => !company.last_activity_at).length,
    };
  }, [companies, companiesQuery.data?.total]);
  const fallbackCharts = useMemo(() => {
    const monthCounts = new Map<string, number>();
    for (const company of companies) {
      const month = company.created_at?.slice(0, 7) || "Unknown";
      monthCounts.set(month, (monthCounts.get(month) ?? 0) + 1);
    }
    const companyGrowth = Array.from(monthCounts.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([month, value]) => ({ month, value }));
    return {
      company_growth: companyGrowth,
      revenue_growth: [],
      credit_consumption: [],
      article_publishing_trends: [],
      active_users_trends: [],
    };
  }, [companies]);

  useEffect(() => {
    console.info("[companies] platform management center", {
      isPlatformOwner,
      returned: companies.length,
      total: companiesQuery.data?.total ?? 0,
      filters: { search, status: statusFilter || null },
      diagnostics: companiesQuery.data?.diagnostics ?? dashboard.data?.diagnostics ?? null,
      error: companiesQuery.error instanceof Error ? companiesQuery.error.message : dashboard.error instanceof Error ? dashboard.error.message : null,
    });
  }, [companies.length, companiesQuery.data, companiesQuery.error, dashboard.data, dashboard.error, isPlatformOwner, search, statusFilter]);

  const summary = dashboard.data?.summary ?? fallbackSummary;
  const charts = dashboard.data?.charts ?? fallbackCharts;
  const hasError = companiesQuery.error || dashboard.error;
  const diagnostics = companiesQuery.data?.diagnostics ?? dashboard.data?.diagnostics ?? null;
  const returnedCount = diagnostics?.returned_count ?? companies.length;
  const totalCount = diagnostics?.total_count ?? companiesQuery.data?.total ?? companies.length;
  const activeFilterLabel = statusFilter ? titleCase(statusFilter) : "All companies";
  const queryStatus = hasError ? "Error" : diagnostics?.rls_blocked ? "Access blocked" : "Healthy";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/90 backdrop-blur-xl">
        <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Platform Company Management</h1>
            <p className="text-xs text-muted-foreground">Company administration, monitoring, health, billing, and operational control.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm"
              placeholder="Search companies, slugs, owners..."
            />
          </div>
        </div>
      </header>

      <main className="space-y-6 p-6">
        {hasError && (
          <Card className="border-destructive/40">
            <CardContent className="space-y-2 pt-6 text-sm">
              <div className="flex items-center gap-2 font-medium text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Companies query failed
              </div>
              <p className="text-destructive">{errorMessage(companiesQuery.error ?? dashboard.error)}</p>
              {dashboard.error && companies.length > 0 && (
                <p className="text-muted-foreground">Showing list-derived fallback metrics until the dashboard API is available.</p>
              )}
            </CardContent>
          </Card>
        )}

        {diagnostics && (
          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 pt-5 text-xs text-muted-foreground">
              <Badge variant={(diagnostics.is_platform_owner ?? isPlatformOwner) ? "default" : "destructive"}>
                {(diagnostics.is_platform_owner ?? isPlatformOwner) ? "Platform admin" : "Restricted"}
              </Badge>
              <span>{returnedCount} of {totalCount} companies loaded</span>
              <span>Filter: {activeFilterLabel}</span>
              {search.trim() && <span>Search: {search.trim()}</span>}
              <Badge variant={queryStatus === "Healthy" ? "secondary" : "destructive"}>{queryStatus}</Badge>
              {diagnostics.warning && <span className="text-yellow-700">{diagnostics.warning}</span>}
            </CardContent>
          </Card>
        )}

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          {summaryCards.map(([key, label, Icon]) => (
            <Card key={key}>
              <CardContent className="flex items-center gap-3 pt-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-medium uppercase text-muted-foreground">{label}</p>
                  <p className="text-xl font-semibold text-foreground">{formatMetric(summary[key] ?? 0, key.includes("revenue"))}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="grid gap-4 xl:grid-cols-5">
          {chartCards.map(([key, title, variant]) => (
            <Card key={key} className="xl:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{title}</CardTitle>
              </CardHeader>
              <CardContent>
                <MiniChart data={charts[key] ?? []} variant={variant} />
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant={statusFilter === "" ? "default" : "outline"} onClick={() => setStatusFilter("")}>All</Button>
            {statusFilters.map((status) => (
              <Button key={status} size="sm" variant={statusFilter === status ? "default" : "outline"} onClick={() => setStatusFilter(status)}>
                {titleCase(status)}
              </Button>
            ))}
          </div>

          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="text-base">Companies</CardTitle>
              <p className="text-xs text-muted-foreground">{companiesQuery.data?.total ?? 0} total</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Users</TableHead>
                      <TableHead className="text-right">Websites</TableHead>
                      <TableHead className="text-right">Articles</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead className="text-right">Open</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companiesQuery.isLoading ? (
                      <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">Loading companies...</TableCell></TableRow>
                    ) : companies.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">{hasError ? "Could not load companies." : "No companies found."}</TableCell></TableRow>
                    ) : companies.map((company) => (
                      <TableRow key={company.id} className="cursor-pointer" onClick={() => navigate(`/companies/${company.id}`)}>
                        <TableCell>
                          <div className="font-medium">{company.name}</div>
                          <div className="text-xs text-muted-foreground">{company.slug}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{company.owner_name}</div>
                          <div className="text-xs text-muted-foreground">{company.owner_email}</div>
                        </TableCell>
                        <TableCell>{company.subscription_plan_name}</TableCell>
                        <TableCell><StatusBadge status={company.status} /></TableCell>
                        <TableCell className="text-right">{company.member_count}</TableCell>
                        <TableCell className="text-right">{company.website_count}</TableCell>
                        <TableCell className="text-right">{company.article_count}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDate(company.last_activity_at ?? company.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={(event) => { event.stopPropagation(); navigate(`/companies/${company.id}`); }}>
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}

function MiniChart({ data, variant }: { data: Array<{ month: string; value: number }>; variant: string }) {
  const normalized = useMemo(() => data.length ? data : [{ month: "No data", value: 0 }], [data]);
  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        {variant === "line" ? (
          <LineChart data={normalized}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
          </LineChart>
        ) : (
          <AreaChart data={normalized}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.16)" />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === "active" ? "default" : status === "suspended" || status === "archived" ? "destructive" : "secondary";
  return <Badge variant={tone as "default" | "secondary" | "destructive"}>{titleCase(status || "active")}</Badge>;
}

function formatMetric(value: number, money = false) {
  if (money) return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value / 100);
  return new Intl.NumberFormat("fr-FR").format(value);
}

function formatDate(value?: string | null) {
  if (!value) return "Never";
  return new Date(value).toLocaleDateString();
}

function titleCase(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : error ? String(error) : "Unknown error";
}
